import React, { useState, useEffect } from 'react';
import Header from './components/Header/Header.jsx';
import FormStep from './components/FormStep/FormStep.jsx';
import CodeStep from './components/CodeStep/CodeStep.jsx';
import MultiStageRecord from './components/MultiStageRecord/MultiStageRecord.jsx';
import SuccessStep from './components/SuccessStep/SuccessStep.jsx';
import MyVideosStep from './components/MyVideosStep/MyVideosStep.jsx';
import LoadingSpinner from './components/Loading/LoadingSpinner.jsx';
import { testimonyApi } from './services/api.js';
import { logError, parseErrorMessage } from './utils/errors.js';
import './styles/global.css';

// Session storage keys
const SESSION_KEYS = {
  USER_DATA: 'foh_user_session',
  VERIFIED_AT: 'foh_verified_at'
};

// Session expiry: 24 hours
const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000;

function App() {
  const [step, setStep] = useState(1); // 1=form, 2=code, 3=record, 4=success
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    applicantType: 'first-time'
  });
  
  // Authentication token from backend
  const [userToken, setUserToken] = useState(null);
  
  // Testimony data from backend
  const [testimonyId, setTestimonyId] = useState(null);
  const [videoFileName, setVideoFileName] = useState(null);
  // Completed stage data passed to SuccessStep for download
  const [completedUploadData, setCompletedUploadData] = useState([]);

  /**
   * Debug: Log testimonyId changes
   */
  useEffect(() => {
    console.log('📋 TestimonyId updated:', testimonyId);
  }, [testimonyId]);

  /**
   * Load session data on mount
   */
  useEffect(() => {
    const loadSession = async () => {
      try {
        const userData = sessionStorage.getItem(SESSION_KEYS.USER_DATA);
        const verifiedAt = sessionStorage.getItem(SESSION_KEYS.VERIFIED_AT);
        const token = sessionStorage.getItem('foh_user_token');
        const storedTestimonyId = sessionStorage.getItem('foh_testimony_id');
        const storedVideoFileName = sessionStorage.getItem('foh_video_filename');
        
        if (userData && verifiedAt && token) {
          const verifiedTime = parseInt(verifiedAt, 10);
          const now = Date.now();
          
          // Check if session is still valid (within 24 hours)
          if (now - verifiedTime < SESSION_EXPIRY_MS) {
            const parsedData = JSON.parse(userData);
            setFormData(parsedData);
            setUserToken(token);
            setIsAuthenticated(true);
            
            // Go directly to record step - no testimony created yet
            setStep(3);
          } else {
            // Session expired, clear storage
            clearSession();
          }
        }
      } catch (err) {
        logError(err, { step: 'loadSession' });
        clearSession();
      }
    };

    loadSession();
  }, []);

  /**
   * Save session data
   */
  const saveSession = (userData, token) => {
    try {
      sessionStorage.setItem(SESSION_KEYS.USER_DATA, JSON.stringify(userData));
      sessionStorage.setItem(SESSION_KEYS.VERIFIED_AT, Date.now().toString());
      if (token) {
        sessionStorage.setItem('foh_user_token', token);
      }
      setIsAuthenticated(true);
    } catch (err) {
      logError(err, { step: 'saveSession' });
    }
  };

  /**
   * Clear session data
   */
  const clearSession = () => {
    sessionStorage.removeItem(SESSION_KEYS.USER_DATA);
    sessionStorage.removeItem(SESSION_KEYS.VERIFIED_AT);
    sessionStorage.removeItem('foh_user_token');
    sessionStorage.removeItem('foh_testimony_id');
    sessionStorage.removeItem('foh_video_filename');
    setIsAuthenticated(false);
    setUserToken(null);
  };

  /**
   * Handle authentication errors during upload
   */
  const handleAuthError = () => {
    clearSession();
    setTestimonyId(null);
    setVideoFileName(null);
    setStep(1);
    setError('Your session has expired. Please start over.');
  };

  /**
   * Step 1: Send verification code
   */
  const handleSendVerification = async (data) => {
    setIsLoading(true);
    setError('');
    
    try {
      // Save form data for later use
      setFormData(data);
      
      // Send verification code via API
      const response = await testimonyApi.sendVerification(data);
      
      if (response.success) {
        // Move to code verification step
        setStep(2);
      } else {
        setError(parseErrorMessage(response.message || 'Failed to send verification code'));
      }
    } catch (err) {
      setError(parseErrorMessage(err));
      logError(err, { step: 'sendVerification', data });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Step 2: Verify code
   */
  const handleVerifyCode = async (code) => {
    setIsLoading(true);
    setError('');
    
    try {
      // Verify code via API (includes user data)
      const response = await testimonyApi.verifyCode({
        phone: formData.phone,
        code,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email
      });
      
      if (response.success) {
        // Extract and save token (token is directly in response, not nested in data)
        const token = response.token;
        setUserToken(token);
        
        // Save session - user is now authenticated
        saveSession(formData, token);
        
        // Go to record step
        setStep(3);
      } else {
        setError(parseErrorMessage(response.message || 'Invalid verification code'));
      }
    } catch (err) {
      setError(parseErrorMessage(err));
      logError(err, { step: 'verifyCode', phone: formData.phone });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Step 3: Create testimony record (after successful verification)
   */
  const handleCreateTestimony = async (token = null, userData = null) => {
    try {
      // Use provided token or fallback to state
      const authToken = token || userToken;
      // Use provided userData or fallback to state
      const dataToUse = userData || formData;
      
      // Check if token exists
      if (!authToken) {
        throw new Error('Authentication token is missing. Please verify your code again.');
      }
      
      // Create testimony record via API (requires token)
      const response = await testimonyApi.createTestimony(dataToUse, authToken);
      
      if (response.success) {
        // Save testimony ID and video file name
        const id = response.data.id;
        const fileName = response.data.videoFileName;
        
        setTestimonyId(id);
        setVideoFileName(fileName);
        
        // Save to session storage
        sessionStorage.setItem('foh_testimony_id', id);
        sessionStorage.setItem('foh_video_filename', fileName);
        
        // Return the data for caller to decide what to do
        return { id, fileName };
      } else {
        const error = new Error(response.message || 'Failed to create testimony record');
        setError(parseErrorMessage(error));
        throw error;
      }
    } catch (err) {
      setError(parseErrorMessage(err));
      logError(err, { step: 'createTestimony', formData: userData || formData });
      throw err; // Re-throw to be caught by verifyCode
    }
  };

  /**
   * Step 4: Handle video upload completion
   * Now this includes: create testimony → upload → notify backend
   */
  const handleUploadComplete = async (uploadData) => {
    try {
      // uploadData now includes testimonyId from RecordStep
      const { testimonyId: newTestimonyId, videoUrl, duration, transcription, metadataJson, isMultiStage } = uploadData;
      
      console.log('📥 App received upload data:', {
        testimonyId: newTestimonyId,
        videoUrl: videoUrl?.substring(0, 50) + '...',
        duration,
        durationType: typeof duration,
        hasDuration: duration !== null && duration !== undefined,
        metadataJsonDuration: metadataJson?.duration,
        isMultiStage
      });
      
      // For multi-stage uploads, skip notifyUpload since each stage was already notified
      if (!isMultiStage) {
        // Notify backend about video upload (single-video mode only)
        const notifyData = {
          videoUrl,
          duration,
          transcription: transcription || null,
          metadataJson: metadataJson || {}
        };
        
        console.log('🚀 Calling notifyUpload with:', notifyData);
        
        const response = await testimonyApi.notifyUpload(newTestimonyId, notifyData);
        
        if (!response.success) {
          throw new Error(response.message || 'Failed to save video information');
        }
      } else {
        console.log('✅ Multi-stage upload - skipping notifyUpload (stages already notified)');
      }
      
      // Store completed stage data for download buttons in SuccessStep
      if (uploadData.allStages && uploadData.allStages.length > 0) {
        setCompletedUploadData(uploadData.allStages);
      }

      // Clear any stored testimony data (we don't need to persist it)
      sessionStorage.removeItem('foh_testimony_id');
      sessionStorage.removeItem('foh_video_filename');
      setTestimonyId(null);
      setVideoFileName(null);

      // Move to success step
      setStep(4);
    } catch (err) {
      const errorMessage = err.message || 'Failed to complete upload. Please try again.';
      setError(errorMessage);
      logError(err, { step: 'uploadComplete', uploadData });
      throw err;
    }
  };

  /**
   * Resend verification code
   */
  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await testimonyApi.resendVerification(formData);
      
      if (!response.success) {
        setError(response.message || 'Failed to resend code');
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to resend code. Please try again.';
      setError(errorMessage);
      logError(err, { step: 'resendCode', phone: formData.phone });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Reset flow to submit another testimony
   */
  const handleReset = async () => {
    // Clear any testimony data and errors
    sessionStorage.removeItem('foh_testimony_id');
    sessionStorage.removeItem('foh_video_filename');
    setTestimonyId(null);
    setVideoFileName(null);
    setCompletedUploadData([]);
    setError('');
    
    // If user is authenticated, go back to record step
    if (isAuthenticated) {
      setStep(3);
    } else {
      // Not authenticated, start from beginning
      setStep(1);
      setFormData({ firstName: '', lastName: '', phone: '', email: '', applicantType: 'first-time' });
    }
  };

  return (
    <div className="app">
      <Header />
      
      <main>
        {/* Global error message */}
        {error && step !== 3 && ( // RecordStep handles its own errors
          <div style={{ maxWidth: '640px', margin: '0 auto', padding: '0 var(--spacing-lg)' }}>
            <div className="alert alert-error" style={{
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
              padding: 'var(--spacing-md)',
              borderRadius: 'var(--radius-md)',
              marginTop: 'var(--spacing-lg)',
              borderLeft: '4px solid #DC2626'
            }}>
              {error}
            </div>
          </div>
        )}

        {/* Step 1: User Verification Form */}
        {step === 1 && (
          <FormStep 
            onSubmit={handleSendVerification} 
            isLoading={isLoading}
          />
        )}

        {/* Step 2: Code Verification */}
        {step === 2 && (
          <CodeStep 
            phone={formData.phone}
            onVerify={handleVerifyCode}
            onResend={handleResendCode}
            isLoading={isLoading}
          />
        )}

        {/* Step 3: Multi-Stage Video Recording (includes instruction video) */}
        {step === 3 && (
          <MultiStageRecord
            userToken={userToken}
            formData={formData}
            onAllStagesComplete={handleUploadComplete}
            onAuthError={handleAuthError}
            onViewMyVideos={() => setStep(5)}
          />
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <SuccessStep
            onReset={handleReset}
            onViewMyVideos={() => setStep(5)}
            completedStages={completedUploadData}
            userToken={userToken}
          />
        )}

        {/* Step 5: My Videos */}
        {step === 5 && (
          <MyVideosStep
            userToken={userToken}
            onBack={() => setStep(4)}
          />
        )}
      </main>

      {/* Footer */}
      <footer style={{
        textAlign: 'center',
        padding: 'var(--spacing-xl)',
        color: '#9CA3AF',
        fontSize: 'var(--font-size-sm)',
        marginTop: 'auto',
        background: 'transparent'
      }}
      className="footer"
      >
        <p style={{ margin: 0 }}>
          © {new Date().getFullYear()} Folds of Honor. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default App;
